import {
  observable,
  createAtom,
  isObservableProp,
  isObservableArray,
  untracked,
} from 'mobx';

import { analytics } from 'core/analytics';
import { ModelRegistry } from 'core/registry';
import { Cache } from 'core/cache';

export const cacheID = (name, id) => `${name}-${id}`;

const keysToGCFromCache = [];
let cacheGCTask = null;

const runCacheGC = () => {
  keysToGCFromCache.forEach(key => {
    // Interestingly deleting from cache can cause other _unCache to happen
    // and append to this array, the array can grow while looping.
    // I tried deferring those extra keys to be GC'd in a following run of
    // this method but that caused a timeout loop for some reason.
    const model = Cache.get(key);
    if (model && !model._atom.isBeingObserved) {
      Cache.delete(key);
    }
  });
  keysToGCFromCache.length = 0;
  cacheGCTask = null;
};

const deferredDeleteFromCache = key => {
  keysToGCFromCache.push(key);
  if (!cacheGCTask) {
    cacheGCTask = setTimeout(runCacheGC, 80);
  }
};

// Note: cacheID could use registryName rather than constructor.registry
export class Model {
  /* We can end up over caching data where two of more queries load intersecting
  objects + one of the queries caused further references to be loaded.
  Can be solved by having each loadData request specify properties */
  @observable id;

  @observable deleted;

  static Load(data, loadCache = undefined) {
    const loadModelCache = loadCache || {};
    if (data !== null) {
      if (!data.id) {
        console.error('error: data passed to a model should have an id', data);
        return null;
      }
      let cached;
      untracked(() => {
        cached = Cache.get(cacheID(this.registryName, data.id));
      });
      const instance = cached || new this();
      instance.loadData(data, loadModelCache);
      return instance;
    }
    return null;
    // return new this(); //no data and not cached - provides a dummy model to map to a dummy app
  }

  constructor() {
    if (!this.constructor?.registryName) {
      throw Error('Model classes should have a "registryName"');
    }
    this._atom = createAtom(
      `atom.${this.constructor.registryName}`,
      this._cache,
      this._unCache,
    );
  }

  _cache = () => {
    if (!this.id) {
      console.warn(
        `${this.constructor.registryName}: a model instance should always have an id`,
      );
      return;
    }
    // In general this is not relied upon as the loadData method fills the cache
    // Sometimes however an already loaded Model might be re-observed?
    if (!Cache.get(cacheID(this.constructor.registryName, this.id))) {
      Cache.set(cacheID(this.constructor.registryName, this.id), this);
    }
    // console.log("add to cache ", cacheID(this.constructor.registry, this.id))
  };

  _unCache = () => {
    deferredDeleteFromCache(cacheID(this.constructor.registryName, this.id));
    // Cache.delete(cacheID(this.constructor.registryName, this.id));
  };

  loadData(data, loadCache = undefined) {
    if (data === null) {
      return this;
    }
    const loadModelCache = loadCache || {};
    const loadedModel = loadModelCache[data.id];
    if (!loadedModel) {
      loadModelCache[data.id] = this;
    }

    if (
      data.id &&
      !Cache.get(cacheID(this.constructor.registryName, data.id))
    ) {
      // console.log("add to cache ", cacheID(this.constructor.registry, this.id))
      Cache.set(cacheID(this.constructor.registryName, data.id), this);
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const prop of Object.keys(data)) {
      // eslint-disable-next-line no-continue
      if (prop.startsWith('_') && !isObservableProp(this, prop)) continue;
      let propModel = null;
      if (this.constructor.ModelProps) {
        propModel = this.constructor.ModelProps[prop];
        if (typeof propModel === 'string') {
          propModel = ModelRegistry.get(propModel);
        }
      }
      if (propModel) {
        if (propModel.prototype instanceof Model) {
          // is subclass of model
          let loadableData = data[prop];
          if (loadedModel && data[prop]) {
            loadableData = { id: data[prop].id };
          }
          this[prop] = propModel.Load(loadableData, loadModelCache);
        } else if (propModel.length === 1) {
          // its an array
          // invariant this[prop] isObservableArray
          if (!propModel[0]) {
            // eslint-disable-next-line no-throw-literal
            throw `Undefined Model Array class on ${this.constructor.registryName}`;
          }
          if (typeof propModel[0] === 'string') {
            propModel[0] = ModelRegistry.get(propModel[0]);
          }
          if (!(propModel[0].prototype instanceof Model)) {
            // eslint-disable-next-line no-throw-literal
            throw `Model array class ${propModel[0].constructor.registryName} is not a subclass of Model on ${this.constructor.registryName}`;
          }
          this[prop].clear();
          if (data[prop] === null) {
            data[prop] = [];
          }
          // eslint-disable-next-line no-restricted-syntax
          for (const item of data[prop]) {
            // Unexplained:
            //  Accessing propModel.prototype[val] causes loop - even in untracked!
            //  e.g. untracked(() => {console.log("before ", propModel[0].prototype.id);});
            let loadableItemData = item;
            if (loadedModel && item) {
              loadableItemData = { id: item.id };
            }
            this[prop].push(
              propModel[0].Load(loadableItemData, loadModelCache),
            );
          }
        } else {
          // eslint-disable-next-line no-throw-literal
          throw `Invalid PropModel: "${propModel}"`;
        }
      } else if (isObservableProp(this, prop)) {
        try {
          this[prop] = data[prop];
        } catch (error) {
          if (error instanceof RangeError) {
            console.error(
              'Attempting to set an @observable prop to a circular structure. Maybe the attribute should be @observable.shallow instead?',
            );
          }
          analytics.logError(error, scope => {
            scope.setExtra('model', this.constructor.registryName);
            scope.setExtra('data', data);
            scope.setExtra('prop', prop);
          });
          this[prop] = null;
        }
      }
    }
    // can this.id be undefined here?
    if (this.id !== undefined) {
      this._atom.reportObserved();
    }
    return this;
  }

  toJs(toJsCache, stripDownCircularRefs = false) {
    const js = {};
    const jsCache = toJsCache || {};
    const cachedEntry = jsCache[this.id];
    if (cachedEntry) {
      if (stripDownCircularRefs) {
        return {
          id: cachedEntry.id,
        };
      }
      return cachedEntry;
    }
    jsCache[this.id] = js;
    const modelProps = this.constructor.ModelProps;
    const dummyModel = new this.constructor();
    // eslint-disable-next-line no-restricted-syntax
    for (const prop of Object.keys(this)) {
      if (isObservableProp(this, prop)) {
        if (modelProps && modelProps[prop] && this[prop] !== null) {
          if (isObservableArray(this[prop])) {
            if (this[prop].length !== 0) {
              js[prop] = [];
              // eslint-disable-next-line no-restricted-syntax
              for (const model of this[prop]) {
                js[prop].push(model.toJs(jsCache, stripDownCircularRefs));
              }
            }
          } else if (dummyModel[prop] !== this[prop]) {
            js[prop] = this[prop].toJs(jsCache, stripDownCircularRefs);
          }
        } else if (dummyModel[prop] !== this[prop]) {
          js[prop] = this[prop];
        }
      }
    }
    return js;
  }
}
